import { createPortal } from "react-dom";
import { useMatch } from "react-router-dom";
import type { ActiveCallSession } from "../store/callSessionStore";
import { useCallSessionStore } from "../store/callSessionStore";
import { useScreenWakeLock } from "../hooks/useScreenWakeLock";
import { useInCallPortalTarget } from "../lib/inCallPortal";
import { playCallEnd } from "../lib/sound";
import { recordCallLeft } from "../hooks/useCallActivityRecorder";
import { InCallView } from "./InCallView";
import { FloatingCallWidget } from "./FloatingCallWidget";
import { CallAudioRenderer } from "./CallAudioRenderer";
import { VoicePathMonitor } from "./VoicePathMonitor";
import { CallPipHost } from "./CallPipHost";
import { InCallDevicesProvider } from "../hooks/CallDevicesContext";
import { selectionFromChoices } from "../lib/callDevices";

interface CallRouteAwareRendererProps {
  active: ActiveCallSession;
}

/**
 * Renders InCallView when the user is on /call/<active.slug>; otherwise
 * renders the FloatingCallWidget. Both children share the parent
 * <LiveKitRoom>'s context — switching is a pure children swap, no LK
 * reconnect.
 *
 * Mounted inside the Shell-level <LiveKitRoom> (see main.tsx). Because
 * the Shell-level LiveKitRoom is a sibling of <SuiteAppShell>, rendering
 * InCallView here directly would put it outside the shell's flex chain
 * — visible only by scrolling past the sidebar. Instead, InCallView is
 * portaled into a target div rendered by RoomRoute INSIDE the shell's
 * content slot. Context still follows the React tree, so LiveKit hooks
 * resolve correctly even though the DOM parent is elsewhere.
 */
export function CallRouteAwareRenderer({ active }: CallRouteAwareRendererProps) {
  // Hold the wake lock for the whole session — InCallView mounts and
  // unmounts as the user navigates in/out of /call/<slug>, but the call
  // itself is still running and the user's phone should stay awake.
  useScreenWakeLock(true);

  const clearActive = useCallSessionStore((s) => s.clear);
  const markLeaveIntent = useCallSessionStore((s) => s.markLeaveIntent);
  const match = useMatch("/call/:slug");
  const isOnThisCallRoute = match?.params.slug === active.slug;
  const portalTarget = useInCallPortalTarget();

  // User-initiated end-call from MeetingControls — play the hang-up beep
  // before clearing the session. Pill end-call has its own playCallEnd
  // call inside FloatingCallWidget. The Shell's onDisconnected handler
  // (involuntary disconnect) deliberately does NOT play the beep.
  const handleLeave = () => {
    // Deliberate leave: flag intent BEFORE clearing so the LiveKit
    // disconnect this triggers is not treated as an involuntary drop and
    // does NOT start an auto-rejoin (#1178). clear() preserves the flag.
    recordCallLeft();
    markLeaveIntent();
    playCallEnd();
    clearActive();
  };

  return (
    // Device selection, headset auto-switch and fallback run here (GH-1859),
    // above the InCallView ↔ FloatingCallWidget swap, so minimising the call
    // neither resets the chosen devices nor stops reacting to plug-ins.
    <InCallDevicesProvider initialSelection={selectionFromChoices(active.choices)}>
      {/* Inbound audio playback lives HERE, not inside InCallView, so it keeps
          playing while the user is on another module (InCallView unmounts when
          you leave /call/<slug>, but the call — and its audio — is still
          running). Rendered inside the persistent Shell-level <LiveKitRoom>, so
          it's route-independent — which is also what lets the session volume
          (#1722) hold across the full view ↔ floating widget swap. */}
      <CallAudioRenderer />
      {/* Is my voice getting through? Runs once per call, whatever is on screen. */}
      <VoicePathMonitor report={{ roomId: active.roomId, token: active.token }} />
      {/* The picture-in-picture window lives for the whole call: the host keeps
          a fixed place in the tree, so the swap below only changes its
          children and moving between modules never closes the window. */}
      <CallPipHost slug={active.slug} roomId={active.roomId} liveKitToken={active.token}>
        {isOnThisCallRoute
          ? portalTarget
            ? createPortal(
                <InCallView
                  roomName={active.roomName}
                  roomId={active.roomId}
                  liveKitToken={active.token}
                  joinedAt={active.joinedAt}
                  audioContext={active.audioContext}
                  onLeave={handleLeave}
                />,
                portalTarget,
              )
            : null
          : <FloatingCallWidget />}
      </CallPipHost>
    </InCallDevicesProvider>
  );
}
